import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

const postColumns = [
  "id",
  "tenant_id",
  "title",
  "slug",
  "excerpt",
  "body",
  "cover_image_url",
  "author_name",
  "tags",
  "status",
  "published_at",
  "source",
  "external_id",
  "created_at",
  "updated_at",
] as const;

export interface BlogPost {
  id: string;
  tenant_id: string;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  cover_image_url: string | null;
  author_name: string;
  tags: string[];
  status: string;
  published_at: string | null;
  source: string;
  external_id: string | null;
  created_at: string;
  updated_at: string;
}

const editorSchema = z.object({
  id: z.string().uuid().optional(),
  title: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9-]+$/),
  excerpt: z.string().max(500).default(""),
  body: z.string().max(120000).default(""),
  coverImageUrl: z.string().max(1000).default(""),
  authorName: z.string().max(120).default("SquadFlight Crew"),
  tags: z.array(z.string().max(60)).max(12).default([]),
  status: z.enum(["draft", "published"]).default("draft"),
});

/** Public list — published posts only, newest first. */
export const listPublishedPosts = createServerFn({ method: "GET" }).handler(
  async (): Promise<BlogPost[]> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("blog_posts")
      .select(postColumns.join(","))
      .eq("status", "published")
      .lte("published_at", new Date().toISOString())
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) throw new Error("Could not load articles.");
    return (data ?? []) as unknown as BlogPost[];
  },
);

export const getPostBySlug = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => {
    const parsed = z.object({ slug: z.string().min(1).max(200) }).safeParse(data);
    if (!parsed.success) throw new Error("Invalid article.");
    return parsed.data;
  })
  .handler(async ({ data }): Promise<BlogPost | null> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows, error } = await supabaseAdmin.rpc("blog_post_by_slug", {
      _slug: data.slug,
    });
    if (error) throw new Error("Could not load article.");
    return (rows?.[0] as unknown as BlogPost | undefined) ?? null;
  });

/** Crew view — drafts included. */
export const listAllPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BlogPost[]> => {
    const { data, error } = await context.supabase
      .from("blog_posts")
      .select(postColumns.join(","))
      .order("created_at", { ascending: false });
    if (error) throw new Error("Could not load articles.");
    return (data ?? []) as unknown as BlogPost[];
  });

export const saveBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const parsed = editorSchema.safeParse(data);
    if (!parsed.success) throw new Error("Please check the article details.");
    return parsed.data;
  })
  .handler(async ({ data, context }): Promise<{ id: string; slug: string; status: string }> => {
    const { defaultTenantId } = await import("@/lib/tenants.functions");
    const tenantId = await defaultTenantId(context.supabase);

    let publishedAt: string | null = null;
    if (data.status === "published") {
      if (data.id) {
        const { data: existing } = await context.supabase
          .from("blog_posts")
          .select("published_at")
          .eq("id", data.id)
          .maybeSingle();
        publishedAt = existing?.published_at ?? new Date().toISOString();
      } else {
        publishedAt = new Date().toISOString();
      }
    }

    const { data: post, error } = await context.supabase
      .from("blog_posts")
      .upsert(
        {
          id: data.id,
          tenant_id: tenantId,
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          body: data.body,
          cover_image_url: data.coverImageUrl.trim() || null,
          author_name: data.authorName.trim() || "SquadFlight Crew",
          tags: data.tags,
          status: data.status,
          published_at: publishedAt,
        } as never,
        { onConflict: "id" },
      )
      .select("id, slug, status")
      .single();
    if (error || !post) {
      console.error("saveBlogPost error:", error);
      throw new Error("Could not save the article.");
    }
    return { id: post.id, slug: post.slug, status: post.status };
  });

export const deleteBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => {
    const parsed = z.object({ id: z.string().uuid() }).safeParse(data);
    if (!parsed.success) throw new Error("Invalid article.");
    return parsed.data;
  })
  .handler(async ({ data, context }): Promise<{ ok: true }> => {
    const { error } = await context.supabase.from("blog_posts").delete().eq("id", data.id);
    if (error) throw new Error("Could not delete the article.");
    return { ok: true };
  });
